import { useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
    Drawer,
    Box,
    Typography,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Button,
    LinearProgress,
    CircularProgress,
    Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useGetProjectDocumentsQuery, useUploadProjectDocumentMutation, useDeleteProjectDocumentMutation } from 'store/slices/apiSlice';
import { openSnackbar } from 'store/slices/snackbar';

function safeSnackbar(dispatch, payload) {
    try {
        if (typeof openSnackbar === 'function') dispatch(openSnackbar(payload));
    } catch (_) {}
}

const PROJECT_NAME = 'document';
const ACCEPT = '.pdf,.txt,.md,.rst,.docx,.xlsx,.zip';

function getDocFolderPath(doc) {
    if (doc == null) return '';
    const name = typeof doc === 'string' ? doc : doc?.fullName || doc?.name;
    return name && name !== 'default' ? String(name).replaceAll('/', '') : '';
}

export default function EmbeddingDocsManager({ open, onClose, doc, userToken }) {
    const dispatch = useDispatch();
    const folderPath = getDocFolderPath(doc);
    const enabled = open && !!folderPath;
    const { data: rawList = [], isLoading } = useGetProjectDocumentsQuery(
        { project_name: PROJECT_NAME, folder_path: folderPath, user_token: userToken },
        { skip: !enabled }
    );
    const [uploadDoc, { isLoading: isUploading }] = useUploadProjectDocumentMutation();
    const [deleteDoc] = useDeleteProjectDocumentMutation();
    const [deletingId, setDeletingId] = useState(null);
    const fileInputRef = useRef(null);

    const list = Array.isArray(rawList) ? rawList : [];
    const files = list.filter((item) => item.type === 'file');

    const handleUploadClick = () => fileInputRef.current?.click();

    const handleFileChange = async (e) => {
        const files = e.target.files;
        if (!files?.length || !folderPath || !userToken) return;
        const formData = new FormData();
        formData.append('user_token', userToken);
        formData.append('project_name', PROJECT_NAME);
        formData.append('folder_name', `${PROJECT_NAME}/${folderPath}`);
        formData.append('labels', '');
        for (let i = 0; i < files.length; i++) {
            formData.append('files', files[i]);
        }
        try {
            await uploadDoc(formData).unwrap();
            safeSnackbar(dispatch, { open: true, message: 'Đã thêm tài liệu. Dữ liệu sẽ được embedding.', variant: 'alert', alert: { color: 'success' } });
        } catch (err) {
            safeSnackbar(dispatch, { open: true, message: err?.data?.detail || 'Tải lên thất bại.', variant: 'alert', alert: { color: 'error' } });
        }
        e.target.value = '';
    };

    const handleDelete = async (file) => {
        if (!file?.file_name || !userToken) return;
        setDeletingId(file.file_path);
        const formData = new FormData();
        formData.append('user_token', userToken);
        formData.append('project_name', PROJECT_NAME);
        formData.append('folder_name', folderPath);
        formData.append('file_name', file.file_name);
        try {
            await deleteDoc(formData).unwrap();
            safeSnackbar(dispatch, { open: true, message: 'Đã xóa tài liệu.', variant: 'alert', alert: { color: 'success' } });
        } catch (err) {
            safeSnackbar(dispatch, { open: true, message: err?.data?.detail || 'Xóa thất bại.', variant: 'alert', alert: { color: 'error' } });
        }
        setDeletingId(null);
    };

    return (
        <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 400 } } }}>
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h6">Tài liệu embedding</Typography>
                <IconButton onClick={onClose} size="small" aria-label="đóng">
                    <CloseIcon />
                </IconButton>
            </Box>
            <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="body2" color="text.secondary">
                    Trợ lý: <strong>{folderPath || '—'}</strong>. Các file tại đây được dùng để cá nhân hóa chatbot.
                </Typography>
            </Box>
            <Divider />
            <Box sx={{ px: 2, py: 1.5 }}>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPT}
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                />
                <Button fullWidth variant="outlined" startIcon={<UploadFileIcon />} onClick={handleUploadClick} disabled={!folderPath || isUploading}>
                    Thêm tài liệu
                </Button>
                {isUploading && <LinearProgress sx={{ mt: 1 }} />}
            </Box>
            <Divider />
            <Box sx={{ flex: 1, overflow: 'auto' }}>
                {isLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4, gap: 2 }}>
                        <CircularProgress size={32} thickness={4} />
                        <Typography variant="body2" color="text.secondary">Đang tải tài liệu...</Typography>
                    </Box>
                ) : files.length === 0 ? (
                    <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                        <Typography variant="body2">Chưa có tài liệu. Thêm file PDF, Word, TXT, v.v. để chatbot học nội dung.</Typography>
                    </Box>
                ) : (
                    <List dense>
                        {files.map((file) => (
                            <ListItem key={file.file_path || file.file_name}>
                                <ListItemText
                                    primary={file.file_name}
                                    secondary={file.file_size ? `${Number(file.file_size)} MB` : file.file_type || ''}
                                />
                                <ListItemSecondaryAction>
                                    <IconButton
                                        edge="end"
                                        size="small"
                                        onClick={() => handleDelete(file)}
                                        disabled={deletingId === file.file_path}
                                        aria-label="xóa"
                                    >
                                        <DeleteOutlineIcon />
                                    </IconButton>
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                    </List>
                )}
            </Box>
        </Drawer>
    );
}
